import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import firebaseApp from "./Firecontact";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "bootstrap/dist/css/bootstrap.min.css";
import { Helmet } from "react-helmet-async";
import { useDataContext } from "./DataContext";

/* ---------------- VALIDATIONS ---------------- */
const validatePhoneNumber = (phoneNumber) => /^[0-9]{10}$/.test(phoneNumber);

const isValidURL = (url) => {
  const pattern = /^(https?:\/\/)?([a-z0-9.-]+)\.[a-z]{2,6}(\/[^\s]*)?$/i;
  return pattern.test(url);
};

/* ---------------- SKELETON CARD ---------------- */
const ProductSkeleton = () => {
  return (
    <div className="productCard rounded h-100">
      <div className="card_product rounded">
        {/* Image Skeleton */}
        <div
          className="bg-light rounded-top w-100"
          style={{ height: "220px" }}
        />

        {/* Content Skeleton */}
        <div className="border-yellow p-2 text-center">
          <div className="placeholder-glow mb-2">
            <span className="placeholder col-8" style={{ opacity: 0.1 }}></span>
          </div>

          <div className="placeholder-glow mb-2">
            <span className="placeholder col-5 me-2" style={{ opacity: 0.1 }}></span>
            <span className="placeholder col-3" style={{ opacity: 0.1 }}></span>
          </div>

          <div className="placeholder-glow">
            <span className="placeholder col-4" style={{ opacity: 0.1 }}></span>
          </div>
        </div>

        {/* Button Skeleton */}
        <div className="px-2 pb-2">
          <div
            className="bg-secondary rounded"
            style={{ height: "38px", opacity: 0.1 }}
          />
        </div>
      </div>
    </div>
  );
};

/* ---------------- MAIN COMPONENT ---------------- */

const ProductCard = () => {
  const navigate = useNavigate();
  const {
    products: cachedProducts,
    fetchProducts,
    fetchMoreProducts,
    hasMoreProducts
  } = useDataContext();

  const [visibleProducts, setVisibleProducts] = useState([]);
  const [imageIndices, setImageIndices] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const loaderRef = useRef(null);

  const [showModal, setShowModal] = useState(false);
  const [selectedCard] = useState("");
  const [validationErrors, setValidationErrors] = useState({});

  const [formData, setFormData] = useState({
    firstName: "",
    phone: "",
    businessName: "",
    businessLink: "",
    city: "",
    address: "",
    forCard: "",
  });

  /* ---------------- FETCH PRODUCTS FROM FIREBASE (PAGINATED) ---------------- */
  useEffect(() => {
    const loadProducts = async () => {
      // If cached products exist, show ALL immediately (user already viewed them)
      if (cachedProducts && cachedProducts.length > 0) {
        setVisibleProducts(cachedProducts);
        initializeImageIndices(cachedProducts);
        setLoading(false);
        return;
      }

      // First time visit - fetch first batch from Firebase
      setLoading(true);
      const fetchedProducts = await fetchProducts();

      if (fetchedProducts && fetchedProducts.length > 0) {
        setVisibleProducts(fetchedProducts);
        initializeImageIndices(fetchedProducts);
      }
      setLoading(false);
    };

    loadProducts();
  }, [cachedProducts, fetchProducts]);

  // Load more products when scrolling (fetches from Firebase)
  const loadMoreProducts = useCallback(async () => {
    if (loadingMore || !hasMoreProducts) return;

    const previousLength = visibleProducts.length;
    setLoadingMore(true);
    const updatedProducts = await fetchMoreProducts();

    // Only update if we got new products
    if (updatedProducts && updatedProducts.length > previousLength) {
      setVisibleProducts(updatedProducts);
      initializeImageIndices(updatedProducts);
    }
    setLoadingMore(false);
  }, [loadingMore, hasMoreProducts, fetchMoreProducts, visibleProducts.length]);

  // Intersection Observer for infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMoreProducts && !loadingMore) {
          loadMoreProducts();
        }
      },
      { threshold: 0.1 }
    );

    if (loaderRef.current) {
      observer.observe(loaderRef.current);
    }

    return () => observer.disconnect();
  }, [hasMoreProducts, loadingMore, loadMoreProducts]);

  const initializeImageIndices = (products) => {
    const indices = {};
    products.forEach((p) => (indices[p.id] = 0));
    setImageIndices(indices);
  };

  /* ---------------- IMAGE SLIDER ---------------- */
  useEffect(() => {
    if (!visibleProducts.length) return;

    const interval = setInterval(() => {
      setImageIndices((prev) => {
        const updated = { ...prev };
        visibleProducts.forEach((p) => {
          const imgs = p.images || [p.image];
          updated[p.id] = (prev[p.id] + 1) % imgs.length;
        });
        return updated;
      });
    }, 3000);

    return () => clearInterval(interval);
  }, [visibleProducts]);


  const handleClose = () => {
    setFormData({
      firstName: "",
      phone: "",
      businessName: "",
      businessLink: "",
      city: "",
      address: "",
      forCard: "",
    });
    setValidationErrors({});
    setShowModal(false);
  };

  /* ---------------- FORM HANDLING ---------------- */
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setValidationErrors((prev) => ({ ...prev, [name]: "" }));
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = () => {
    const errors = {};

    if (!formData.firstName.trim()) errors.firstName = "First name is required.";
    if (!formData.businessName.trim())
      errors.businessName = "Business name is required.";
    if (!formData.businessLink.trim())
      errors.businessLink = "Business link is required.";
    else if (!isValidURL(formData.businessLink))
      errors.businessLink = "Enter a valid URL.";
    if (!formData.phone.trim())
      errors.phone = "Phone number is required.";
    else if (!validatePhoneNumber(formData.phone))
      errors.phone = "Enter valid 10-digit number.";
    if (!formData.city.trim()) errors.city = "City is required.";
    if (!formData.address.trim()) errors.address = "Address is required.";

    setValidationErrors(errors);
    if (Object.keys(errors).length > 0) return;

    const db = firebaseApp.firestore();

    db.collection("Product_orders")
      .add({
        ...formData,
        Date: new Date().toLocaleString(),
      })
      .then(() => {
        toast.success("Order submitted successfully!");
        handleClose();
      })
      .catch((err) => console.error(err));
  };

  /* ---------------- UI ---------------- */
  return (
    <>
      <Helmet>
        <title>9R NFC Card | Smart NFC Business Cards</title>
      </Helmet>

      <section className="pb-35 pt-35 bg-white service-categories">
        <div className="container">
          <div className="text-center mb-4">
            <h2 className="fw-semibold">NFC Card <span className="text-orange">Services</span></h2>
            <p className="text-muted">Smart tap-enabled NFC business cards for professionals.</p>
          </div>
          {/* ---------- SKELETON / PRODUCTS ---------- */}
          {loading ? (
            <div className="featureCategorySlider pt-1 feature-category-grid">
              {Array.from({ length: 10 }).map((_, i) => (
                <ProductSkeleton key={i} />
              ))}
            </div>
          ) : (
            <>
              <div className="featureCategorySlider pt-1 feature-category-grid">
                {visibleProducts.map((product) => {
                  const images = product.images || [product.image];
                  const index = imageIndices[product.id] || 0;

                  return (
                    <div key={product.id} className="productCard rounded h-100">
                      <div
                        className="card_product rounded p-0"
                        style={{ cursor: "pointer" }}
                        onClick={() => navigate(`/product/${product.id}`)}
                      >
                        <img
                          src={images[index]}
                          alt={product.productName}
                          className="rounded-top w-100"
                          loading="lazy"
                        />

                        <div className="border-yellow p-2 text-center card-body">
                          <span className="fw-500">
                            {product.productName}
                          </span>

                          <div>
                            <span className="text-danger text-decoration-line-through pe-1">
                              ₹{product.price}
                            </span>
                            <span className="text-success">
                              ({product.offerPrice}% OFF)
                            </span>
                          </div>

                          <div className="fw-bold">
                            ₹{product.finalPrice}
                          </div>
                        </div>


                        <div className="px-2 pb-2">
                          <button className="btn buy-now-btn rounded w-100">
                            VIEW PRODUCT
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Infinite Scroll Loader - Only show when loading more */}
              {hasMoreProducts && loadingMore && (
                <div ref={loaderRef} className="featureCategorySlider pt-3 feature-category-grid">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <ProductSkeleton key={`loader-${i}`} />
                  ))}
                </div>
              )}

              {/* Hidden loader trigger for intersection observer */}
              {hasMoreProducts && !loadingMore && (
                <div ref={loaderRef} style={{ height: "1px" }} />
              )}
            </>
          )}
        </div>
      </section>

      {/* ---------------- MODAL ---------------- */}
      <Modal show={showModal} onHide={handleClose} centered>
        <Modal.Header closeButton>
          <Modal.Title>Order Now for {selectedCard}</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {[
            "firstName",
            "businessName",
            "businessLink",
            "phone",
            "city",
            "address",
          ].map((field) => (
            <div className="mb-3" key={field}>
              <input
                type="text"
                name={field}
                value={formData[field]}
                onChange={handleInputChange}
                placeholder={field}
                className={`form-control ${validationErrors[field] ? "is-invalid" : ""
                  }`}
              />
              <div className="invalid-feedback">
                {validationErrors[field]}
              </div>
            </div>
          ))}

          <input
            type="text"
            value={formData.forCard}
            className="form-control"
            readOnly
          />
        </Modal.Body>

        <Modal.Footer>
          <Button onClick={handleSubmit}>Order Now</Button>
          <Button variant="secondary" onClick={handleClose}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ProductCard;
